/**
 * PropertiesPanel - Shows properties for selected sprite, dialog, or scene
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSceneStore } from '../../../stores/sceneStore';
import { useModalContext } from '../../../contexts/ModalContext';
import { useApiClient } from '../../../api/client';
import MediaPicker from '../../../modals/MediaPicker';
import BackgroundEditor from '../modals/BackgroundEditor';
import SheetImportDialog from '../../sheets/SheetImportDialog';
import SheetEditor, { SHEET_EDITOR_TITLE } from '../../sheets/SheetEditor';
import StatesSection from '../../sheets/StatesSection';
import MotionSection from './MotionSection';
import TransitionFields from './TransitionFields';
import BackgroundStatesSection, { stateErrorText } from './BackgroundStatesSection';
import SceneNameInput from '../SceneNameInput';
import type { Dialog, DialogLine, DialogTransition, Transition, Sound, SoundType, Orientation, Position, Transform, Hinge } from '../../../../../Shared/src/@types';
import {
  getResponsivePosition,
  getResponsiveTransform,
  isFieldOverride,
  removeFieldOverride,
} from '../../../utils/responsive';
import { loadImageNaturalSize, resolveSpriteSize, sizeFromImage, SPRITE_POSITION_RANGE } from '../../../utils/spriteGeometry';
import { pictureSizeOf, useMediaSheetStore } from '../../../stores/mediaSheetStore';
import type { SpriteStateError } from '../../../stores/sceneStore';
import { parseOptionalSeconds, formatOptionalSeconds, withLineStartTime, fittedLineSeconds, lineInMs } from '../../../../../Shared/src/utils/sceneTiming';
import { formatMediaLength } from '../../media/mediaListing';
import type { MediaItem } from '../../../@types/Media';
import styles from './PropertiesPanel.module.scss';

/**
 * A seconds field that commits on blur or Enter (spec 007): the number is
 * milliseconds in the store, an empty field is `undefined`. Typing does not
 * clamp mid-number; the store clamps the committed value.
 */
const SecondsInput: React.FC<{
  valueMs: number | undefined;
  onCommit: (ms: number | undefined) => void;
  placeholder?: string;
  title?: string;
  testId?: string;
  disabled?: boolean;
}> = ({ valueMs, onCommit, placeholder, title, testId, disabled }) => {
  const [text, setText] = useState(formatOptionalSeconds(valueMs));
  useEffect(() => setText(formatOptionalSeconds(valueMs)), [valueMs]);
  const commit = () => {
    const ms = parseOptionalSeconds(text);
    if (ms !== valueMs) onCommit(ms);
    else setText(formatOptionalSeconds(valueMs));
  };
  return (
    <input
      type="number"
      min={0}
      step={0.1}
      value={text}
      placeholder={placeholder}
      title={title}
      disabled={disabled}
      data-testid={testId}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
};

// Generate simple unique IDs for dialog lines
const generateId = () => Math.random().toString(36).substring(2, 9);

// Sound type options
const SOUND_TYPES: { value: SoundType; label: string }[] = [
  { value: 'bgm', label: 'Background Music' },
  { value: 'sfx', label: 'Sound Effect' },
  { value: 'voice', label: 'Voice' },
];

// Dialog transition options
const DIALOG_TRANSITION_OPTIONS: { value: DialogTransition; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'typing', label: 'Typing' },
  { value: 'fade', label: 'Fade' },
];

// Default transition values
const DEFAULT_TRANSITION: Transition = {
  type: 'none',
  duration: 500,
  ease: 'power1.out',
  direction: 'left',
};




/**
 * A voiced line's voice length (spec 041): `voice 6.6 s` or `voice length
 * unknown` once its metadata is read, and "Fit to voice" while the line's
 * duration differs from the fitted value and the length is known.
 */
const VoiceLength: React.FC<{
  line: DialogLine;
  /** The line's in-transition length in its dialog (`lineInMs`, spec 041 D-5). */
  inMs: number;
  lengthMs: number | undefined;
  known: boolean;
  onFit: (lengthMs: number) => void;
}> = ({ line, inMs, lengthMs, known, onFit }) => {
  if (lengthMs === undefined) {
    return known ? <span className={styles.voiceLength} data-testid="line-voice-length">voice length unknown</span> : null;
  }
  const fitted = fittedLineSeconds(lengthMs, inMs);
  return (
    <>
      <span className={styles.voiceLength} data-testid="line-voice-length">voice {formatMediaLength(lengthMs)}</span>
      {line.duration !== fitted && (
        <button onClick={() => onFit(lengthMs)} title="Make the line last as long as its voice plus a short pause" data-testid="line-fit-voice">
          Fit to voice
        </button>
      )}
    </>
  );
};

export const PropertiesPanel: React.FC = () => {
  const { addModal, removeModal } = useModalContext();
  const apiClient = useApiClient();
  const [newStateName, setNewStateName] = useState('');
  const backgroundStatesRef = useRef<HTMLInputElement>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingLineText, setEditingLineText] = useState('');
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const pannerRef = useRef<StereoPannerNode | null>(null);

  // Store state - scene now directly contains sprites, dialogs, etc.
  const selection = useSceneStore((state) => state.selection);
  const scene = useSceneStore((state) => state.scene);

  // Store actions
  const updateSprite = useSceneStore((state) => state.updateSprite);
  const updateDialog = useSceneStore((state) => state.updateDialog);
  const attachLineVoice = useSceneStore((state) => state.attachLineVoice);
  const removeLineVoice = useSceneStore((state) => state.removeLineVoice);
  const setLineDuration = useSceneStore((state) => state.setLineDuration);
  const fitLineToVoiceLength = useSceneStore((state) => state.fitLineToVoiceLength);
  // Voice lengths (spec 041), read with each voice's metadata.
  const mediaLengths = useMediaSheetStore((state) => state.lengths);
  const mediaKnown = useMediaSheetStore((state) => state.known);
  const updateSound = useSceneStore((state) => state.updateSound);
  const setSoundType = useSceneStore((state) => state.setSoundType);
  const setSoundTiming = useSceneStore((state) => state.setSoundTiming);
  const deleteSignal = useSceneStore((state) => state.deleteSignal);
  const updateSceneProperties = useSceneStore((state) => state.updateSceneProperties);

  // Check if scene is selected
  const isSceneSelected = selection.type === 'scene';
  const [activeTab, setActiveTab] = useState<'properties' | 'transitions'>('properties');

  const tabBar = (
    <div className={styles.tabBar}>
      <button
        className={`${styles.tab} ${activeTab === 'properties' ? styles.tabActive : ''}`}
        onClick={() => setActiveTab('properties')}
      >
        Properties
      </button>
      <button
        className={`${styles.tab} ${activeTab === 'transitions' ? styles.tabActive : ''}`}
        onClick={() => setActiveTab('transitions')}
      >
        Transitions
      </button>
    </div>
  );

  // Get selected sprite
  const selectedSprite = selection.type === 'sprite' && selection.ids.length === 1
    ? scene?.sprites.find((s) => s.id === selection.ids[0])
    : null;

  // Get selected dialog
  const selectedDialog = selection.type === 'dialog' && selection.ids.length === 1
    ? scene?.dialogs.find((d) => d.id === selection.ids[0])
    : null;

  // The voice lengths of the selected dialog's lines (spec 041): metadata only, never the file.
  const voiceIds = (selectedDialog?.lines ?? []).map((l) => l.audioId).filter((id): id is number => typeof id === 'number' && id > 0).join(',');
  useEffect(() => {
    if (!voiceIds) return;
    void useMediaSheetStore.getState().loadSheets(voiceIds.split(',').map(Number));
  }, [voiceIds]);

  // Get selected sound
  const selectedSound = selection.type === 'sound' && selection.ids.length === 1
    ? scene?.sounds.find((s) => s.id === selection.ids[0])
    : null;

  // Get selected signal (read-only, spec 007)
  const selectedSignal = selection.type === 'signal' && selection.ids.length === 1
    ? scene?.signals.find((s) => s.id === selection.ids[0])
    : null;

  // Sprite states go through store actions (spec 005)
  const addSpriteState = useSceneStore((state) => state.addSpriteState);
  const removeSpriteState = useSceneStore((state) => state.removeSpriteState);
  const setDefaultSpriteState = useSceneStore((state) => state.setDefaultSpriteState);
  const assignStateMedia = useSceneStore((state) => state.assignStateMedia);
  const [stateError, setStateError] = useState<SpriteStateError | null>(null);

  // Add a new state to the sprite; a refused name shows an inline message
  const handleAddState = useCallback(() => {
    if (!selectedSprite) return;
    const error = addSpriteState(selectedSprite.id, newStateName);
    setStateError(error);
    if (!error) setNewStateName('');
  }, [selectedSprite, newStateName, addSpriteState]);

  const handleDeleteState = useCallback((stateName: string) => {
    if (!selectedSprite) return;
    setStateError(removeSpriteState(selectedSprite.id, stateName));
  }, [selectedSprite, removeSpriteState]);

  const handleSetDefaultState = useCallback((stateName: string) => {
    if (!selectedSprite) return;
    setDefaultSpriteState(selectedSprite.id, stateName);
  }, [selectedSprite, setDefaultSpriteState]);

  // Open media picker for a state. The image's natural size gives a sprite
  // without a size its proportions (assignStateMedia).
  const handlePickMedia = useCallback((stateName: string) => {
    if (!selectedSprite) return;
    const spriteId = selectedSprite.id;

    addModal({
      title: `Select Image for "${stateName}"`,
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={async (mediaId: number) => {
            removeModal();
            let natural: { width: number; height: number } | undefined;
            try {
              // The natural size; assignStateMedia sizes a spritesheet by its frame (spec 033).
              [natural] = await Promise.all([
                loadImageNaturalSize(await apiClient.getMediaFileBlob(mediaId)),
                useMediaSheetStore.getState().loadSheets([mediaId]),
              ]);
            } catch {
              natural = undefined; // the image still gets assigned; the size stays as it is
            }
            assignStateMedia(spriteId, stateName, mediaId, natural);
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
    });
  }, [selectedSprite, addModal, removeModal, assignStateMedia, apiClient]);

  // Import a sprite tool's spritesheet export as states (spec 034); from a
  // state's card, a single animation fills that state (spec 035).
  const handleImportSheet = useCallback((targetState?: string) => {
    if (!selectedSprite) return;
    addModal({
      title: `Import spritesheet · ${selectedSprite.name}`,
      body: <SheetImportDialog spriteId={selectedSprite.id} targetState={targetState} onClose={() => removeModal()} />,
      size: 'xl',
      theme: 'dark',
    });
  }, [selectedSprite, addModal, removeModal]);

  // The sheet editor (spec 035): `assign` points the state at the picture on Done.
  const handleEditSheet = useCallback((stateName: string, mediaId: number, assign: boolean) => {
    if (!selectedSprite) return;
    addModal({
      title: SHEET_EDITOR_TITLE(selectedSprite.name, stateName),
      body: <SheetEditor spriteId={selectedSprite.id} stateName={stateName} mediaId={mediaId} assign={assign} onClose={() => removeModal()} />,
      size: 'xl',
      theme: 'dark',
    });
  }, [selectedSprite, addModal, removeModal]);

  // "Pick a sheet from Media…" (spec 035): a picture with a cut is assigned
  // (one undo step); one without opens the sheet editor to cut it.
  const handlePickSheet = useCallback((stateName: string) => {
    if (!selectedSprite) return;
    const spriteId = selectedSprite.id;
    addModal({
      title: `Pick a sheet for "${stateName}"`,
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={async (mediaId: number) => {
            removeModal();
            const cuts = await useMediaSheetStore.getState().loadSheets([mediaId]);
            if (!cuts[mediaId]) {
              handleEditSheet(stateName, mediaId, true);
              return;
            }
            let natural: { width: number; height: number } | undefined;
            try {
              natural = await loadImageNaturalSize(await apiClient.getMediaFileBlob(mediaId));
            } catch {
              natural = undefined;
            }
            assignStateMedia(spriteId, stateName, mediaId, natural);
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
    });
  }, [selectedSprite, addModal, removeModal, assignStateMedia, apiClient, handleEditSheet]);

  // Update sprite name
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSprite) return;
    updateSprite(selectedSprite.id, { name: e.target.value });
  }, [selectedSprite, updateSprite]);

  // Update sprite z-index
  const handleZIndexChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSprite) return;
    // An integer ≥ 0 (spec 005)
    const value = Math.max(0, Math.round(parseFloat(e.target.value) || 0));
    updateSprite(selectedSprite.id, { zIndex: value });
  }, [selectedSprite, updateSprite]);

  // Dialog handlers
  const handleDialogNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { name: e.target.value });
  }, [selectedDialog, updateDialog]);

  const handleDialogPositionChange = useCallback((position: 'top' | 'bottom') => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { position });
  }, [selectedDialog, updateDialog]);

  const handleAddLine = useCallback(() => {
    if (!selectedDialog) return;
    const newLine: DialogLine = { id: generateId(), text: '', duration: 3 };
    updateDialog(selectedDialog.id, {
      lines: [...selectedDialog.lines, newLine],
    });
  }, [selectedDialog, updateDialog]);

  const handleDeleteLine = useCallback((lineId: string) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, {
      lines: selectedDialog.lines.filter((l) => l.id !== lineId),
    });
  }, [selectedDialog, updateDialog]);

  const handleEditLineText = useCallback((lineId: string, currentText?: string) => {
    setEditingLineId(lineId);
    setEditingLineText(currentText || '');
  }, []);

  const handleSaveLineText = useCallback(() => {
    if (!selectedDialog || !editingLineId) return;
    const newLines = selectedDialog.lines.map((l) =>
      l.id === editingLineId ? { ...l, text: editingLineText } : l
    );
    updateDialog(selectedDialog.id, { lines: newLines });
    setEditingLineId(null);
    setEditingLineText('');
  }, [selectedDialog, editingLineId, editingLineText, updateDialog]);

  // A line's voice (spec 041): the picked item's length fits the line; no file is downloaded to measure it.
  const handlePickAudio = useCallback((lineId: string) => {
    if (!selectedDialog) return;
    const dialogId = selectedDialog.id;
    addModal({
      title: 'Select Audio',
      body: (
        <MediaPicker
          typeFilter="audio"
          lockFilter
          onSelect={(mediaId: number, item?: MediaItem) => {
            removeModal();
            const sheets = useMediaSheetStore.getState();
            const durationMs = item ? item.durationMs : sheets.lengths[mediaId];
            if (item) sheets.setLength(mediaId, item.durationMs);
            attachLineVoice(dialogId, lineId, mediaId, durationMs);
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
    });
  }, [selectedDialog, addModal, removeModal, attachLineVoice]);

  const handleRemoveAudio = useCallback((lineId: string) => {
    if (!selectedDialog) return;
    removeLineVoice(selectedDialog.id, lineId);
  }, [selectedDialog, removeLineVoice]);

  const handleDurationChange = useCallback((lineId: string, seconds: number) => {
    if (!selectedDialog || !Number.isFinite(seconds) || seconds <= 0) return;
    setLineDuration(selectedDialog.id, lineId, seconds);
  }, [selectedDialog, setLineDuration]);

  // Dialog styling handlers
  const handleDialogTextColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { textColor: e.target.value });
  }, [selectedDialog, updateDialog]);

  const handleDialogOutlineColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { outlineColor: e.target.value });
  }, [selectedDialog, updateDialog]);

  const handleDialogOutlineWidthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDialog) return;
    const width = parseFloat(e.target.value) || 0;
    updateDialog(selectedDialog.id, { outlineWidth: width });
  }, [selectedDialog, updateDialog]);

  const handleDialogInTransitionChange = useCallback((transition: DialogTransition) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { inTransition: transition });
  }, [selectedDialog, updateDialog]);

  const handleDialogOutTransitionChange = useCallback((transition: DialogTransition) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { outTransition: transition });
  }, [selectedDialog, updateDialog]);

  // A line's Start (s): a number sets it, an empty field returns the line to
  // back-to-back timing (spec 007)
  const handleLineStartTimeChange = useCallback((lineId: string, startTime: number | undefined) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { lines: withLineStartTime(selectedDialog.lines, lineId, startTime) });
  }, [selectedDialog, updateDialog]);

  // The dialog's Start (s) is Dialog.startTime, the same value the 💬 bar drags
  const handleDialogStartChange = useCallback((ms: number | undefined) => {
    if (!selectedDialog) return;
    updateDialog(selectedDialog.id, { startTime: ms ?? 0 });
  }, [selectedDialog, updateDialog]);

  // Scene property handlers
  const handleEnterTransitionChange = useCallback((updates: Partial<Transition>) => {
    if (!scene) return;
    const current = scene.enterTransition || DEFAULT_TRANSITION;
    updateSceneProperties({ 
      enterTransition: { ...current, ...updates } as Transition 
    });
  }, [scene, updateSceneProperties]);

  const handleExitTransitionChange = useCallback((updates: Partial<Transition>) => {
    if (!scene) return;
    const current = scene.exitTransition || DEFAULT_TRANSITION;
    updateSceneProperties({ 
      exitTransition: { ...current, ...updates } as Transition 
    });
  }, [scene, updateSceneProperties]);

  // Sound property handlers
  const handleSoundNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSound) return;
    updateSound(selectedSound.id, { name: e.target.value });
  }, [selectedSound, updateSound]);

  const handleSoundTypeChange = useCallback((type: SoundType) => {
    if (!selectedSound) return;
    setSoundType(selectedSound.id, type);
  }, [selectedSound, setSoundType]);

  const handleSoundVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSound) return;
    const volume = parseFloat(e.target.value);
    updateSound(selectedSound.id, { volume });
    // Update playing audio in real-time
    if (previewAudio) {
      previewAudio.volume = volume;
    }
  }, [selectedSound, updateSound, previewAudio]);

  const handleSoundPanChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSound) return;
    const pan = parseFloat(e.target.value);
    updateSound(selectedSound.id, { pan });
    // Update playing audio in real-time
    if (pannerRef.current) {
      pannerRef.current.pan.value = pan;
    }
  }, [selectedSound, updateSound]);

  const handleSoundLoopChange = useCallback((loop: boolean) => {
    if (!selectedSound) return;
    updateSound(selectedSound.id, { loop });
  }, [selectedSound, updateSound]);

  const handlePickSoundMedia = useCallback(() => {
    if (!selectedSound) return;
    addModal({
      title: 'Select Audio File',
      body: (
        <MediaPicker
          typeFilter="audio"
          lockFilter
          onSelect={(mediaId: number) => {
            updateSound(selectedSound.id, { mediaId });
            removeModal();
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'lg',
    });
  }, [selectedSound, addModal, removeModal, updateSound]);

  const handlePreviewSound = useCallback(async () => {
    if (!selectedSound || !selectedSound.mediaId) return;
    
    // If already playing, stop
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      URL.revokeObjectURL(previewAudio.src);
      setPreviewAudio(null);
      pannerRef.current = null;
      return;
    }
    
    setIsLoadingAudio(true);
    
    try {
      // Fetch audio blob through API client
      const blob = await apiClient.getMediaFileBlob(selectedSound.mediaId);
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.volume = selectedSound.volume;
      
      // Handle pan using Web Audio API if available
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        }
        const source = audioContextRef.current.createMediaElementSource(audio);
        const panner = audioContextRef.current.createStereoPanner();
        panner.pan.value = selectedSound.pan;
        source.connect(panner);
        panner.connect(audioContextRef.current.destination);
        pannerRef.current = panner;
      } catch (e) {
        // Fall back to no panning if Web Audio API not available
        console.warn('Web Audio API not available for panning:', e);
      }
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setPreviewAudio(null);
        pannerRef.current = null;
      };
      audio.onerror = (e) => {
        console.error('Audio playback error:', e);
        URL.revokeObjectURL(audioUrl);
        setPreviewAudio(null);
        pannerRef.current = null;
      };
      
      await audio.play();
      setPreviewAudio(audio);
    } catch (error) {
      console.error('Failed to load audio:', error);
    } finally {
      setIsLoadingAudio(false);
    }
  }, [selectedSound, previewAudio, apiClient]);

  const handleStopPreview = useCallback(() => {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      URL.revokeObjectURL(previewAudio.src);
      setPreviewAudio(null);
      pannerRef.current = null;
    }
  }, [previewAudio]);

  // Scene selected - show scene properties
  if (isSceneSelected && scene) {
    const enterTransition = scene.enterTransition || DEFAULT_TRANSITION;
    const exitTransition = scene.exitTransition || DEFAULT_TRANSITION;

    return (
      <div className={styles.panel}>
        {tabBar}

        {activeTab === 'properties' && (
        <div className={styles.section}>
          <div className={styles.field}>
            <label>Name</label>
            <SceneNameInput placeholder="Scene name..." data-testid="scene-properties-name" />
          </div>

          <div className={styles.field}>
            <label>Background</label>
            {/* Spec 040 D-1: the background's states, above the modal's button */}
            <h5 className={styles.statesHeading}>States</h5>
            <BackgroundStatesSection ref={backgroundStatesRef} />
            <button
              className={styles.editBackgroundBtn}
              onClick={() => {
                addModal({
                  title: 'Edit Background',
                  body: (
                    <BackgroundEditor
                      onClose={() => removeModal()}
                      onEditStates={() => {
                        removeModal();
                        // D-2: "Edit states" lands on the States section's add row.
                        setTimeout(() => backgroundStatesRef.current?.focus(), 0);
                      }}
                    />
                  ),
                  size: 'xl',
                  theme: 'dark',
                });
              }}
            >
              🎨 Edit Background
            </button>
          </div>
        </div>
        )}

        {activeTab === 'transitions' && (
        <>
        <div className={styles.section}>
          <h4>Enter Transition</h4>
          
          <TransitionFields value={enterTransition} onChange={handleEnterTransitionChange} fieldClassName={styles.field} />
        </div>

        <div className={styles.section}>
          <h4>Exit Transition</h4>
          
          <TransitionFields value={exitTransition} onChange={handleExitTransitionChange} fieldClassName={styles.field} />
        </div>
        </>
        )}
      </div>
    );
  }

  // Sound selected - show sound properties
  if (selectedSound) {
    return (
      <div className={styles.panel}>
        <div className={styles.section}>
          <h4>Sound Properties</h4>
          
          <div className={styles.field}>
            <label>Name</label>
            <input
              type="text"
              value={selectedSound.name}
              onChange={handleSoundNameChange}
              placeholder="Sound name..."
            />
          </div>

          <div className={styles.field}>
            <label>Type</label>
            <select
              value={selectedSound.type}
              onChange={(e) => handleSoundTypeChange(e.target.value as SoundType)}
            >
              {SOUND_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label>Audio File</label>
            {selectedSound.mediaId > 0 ? (
              <button
                className={styles.changeMediaBtn}
                onClick={handlePickSoundMedia}
              >
                Media #{selectedSound.mediaId} - Change
              </button>
            ) : (
              <>
                <span className={styles.noAudio} data-testid="sound-no-audio">No audio file</span>
                <button
                  className={styles.pickMediaBtn}
                  onClick={handlePickSoundMedia}
                >
                  + Select Audio
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <h4>Timing</h4>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Start (s)</label>
              <SecondsInput
                valueMs={selectedSound.startTime ?? 0}
                onCommit={(ms) => setSoundTiming(selectedSound.id, { startTime: ms ?? 0 })}
                testId="sound-start"
              />
            </div>
            <div className={styles.field}>
              <label>End (s)</label>
              <SecondsInput
                valueMs={selectedSound.endTime}
                onCommit={(ms) => setSoundTiming(selectedSound.id, { endTime: ms })}
                placeholder="scene end"
                title="Empty = plays to the scene end"
                testId="sound-end"
              />
            </div>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Fade in (s)</label>
              <SecondsInput
                valueMs={selectedSound.fadeIn ?? 0}
                onCommit={(ms) => setSoundTiming(selectedSound.id, { fadeIn: ms ?? 0 })}
                testId="sound-fade-in"
              />
            </div>
            <div className={styles.field}>
              <label>Fade out (s)</label>
              <SecondsInput
                valueMs={selectedSound.fadeOut ?? 0}
                onCommit={(ms) => setSoundTiming(selectedSound.id, { fadeOut: ms ?? 0 })}
                testId="sound-fade-out"
              />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h4>Playback</h4>

          <div className={styles.field}>
            <label>Volume: {Math.round(selectedSound.volume * 100)}%</label>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={selectedSound.volume}
              onChange={handleSoundVolumeChange}
            />
          </div>

          <div className={styles.field}>
            <label>Pan: {selectedSound.pan === 0 ? 'Center' : selectedSound.pan < 0 ? `${Math.round(Math.abs(selectedSound.pan) * 100)}% Left` : `${Math.round(selectedSound.pan * 100)}% Right`}</label>
            <input
              type="range"
              min={-1}
              max={1}
              step={0.01}
              value={selectedSound.pan}
              onChange={handleSoundPanChange}
            />
          </div>

          <div className={styles.field}>
            <label>Loop</label>
            <div className={styles.positionToggle}>
              <button
                className={selectedSound.loop ? styles.active : ''}
                onClick={() => handleSoundLoopChange(true)}
              >
                On
              </button>
              <button
                className={!selectedSound.loop ? styles.active : ''}
                onClick={() => handleSoundLoopChange(false)}
              >
                Off
              </button>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h4>Preview</h4>
          <div className={styles.previewControls}>
            <button
              className={previewAudio ? styles.stopBtn : styles.playBtn}
              onClick={previewAudio ? handleStopPreview : handlePreviewSound}
              disabled={!selectedSound.mediaId || isLoadingAudio}
            >
              {isLoadingAudio ? '⏳ Loading...' : previewAudio ? '■ Stop' : '▶ Play'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Signal selected - read-only (spec 007): signals are kept, not authored
  if (selectedSignal) {
    return (
      <div className={styles.panel} data-testid="signal-panel">
        <div className={styles.section}>
          <h4>Signal</h4>
          <div className={styles.field}>
            <label>Type</label>
            <input type="text" value={selectedSignal.type} readOnly data-testid="signal-type" />
          </div>
          <div className={styles.field}>
            <label>Trigger time (s)</label>
            <input type="text" value={formatOptionalSeconds(selectedSignal.trigger?.time) || '—'} readOnly data-testid="signal-time" />
          </div>
          <div className={styles.field}>
            <label>Payload</label>
            <pre className={styles.signalPayload}>{JSON.stringify(selectedSignal.payload ?? {}, null, 2)}</pre>
          </div>
          <p className={styles.signalNote}>Signals are not played yet; they are kept with the scene.</p>
          <button
            className={styles.deleteSignalBtn}
            onClick={() => deleteSignal(selectedSignal.id)}
            data-testid="signal-delete"
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  // Nothing selected
  if (!selectedSprite && !selectedDialog && !selectedSound) {
    return (
      <div className={styles.panel}>
        <div className={styles.empty}>
          <p>Select a sprite, dialog, or sound to edit</p>
        </div>
      </div>
    );
  }

  // Dialog selected
  if (selectedDialog) {
    return (
      <div className={styles.panel}>
        {tabBar}

        {activeTab === 'properties' && (
        <>
        <div className={styles.section}>
          <div className={styles.field}>
            <label>Name</label>
            <input
              type="text"
              value={selectedDialog.name}
              onChange={handleDialogNameChange}
            />
          </div>

          <div className={styles.field}>
            <label>Position</label>
            <div className={styles.positionToggle}>
              <button
                className={selectedDialog.position === 'top' ? styles.active : ''}
                onClick={() => handleDialogPositionChange('top')}
              >
                Top
              </button>
              <button
                className={selectedDialog.position === 'bottom' ? styles.active : ''}
                onClick={() => handleDialogPositionChange('bottom')}
              >
                Bottom
              </button>
            </div>
          </div>

          <div className={styles.field}>
            <label>Start (s)</label>
            <SecondsInput
              valueMs={selectedDialog.startTime ?? 0}
              onCommit={handleDialogStartChange}
              title="When the dialog panel appears (from scene start)"
              testId="dialog-start"
            />
          </div>
        </div>

        <div className={styles.section}>
          <h4>Styling</h4>
          
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Text Color</label>
              <input
                type="color"
                value={selectedDialog.textColor || '#ffffff'}
                onChange={handleDialogTextColorChange}
              />
            </div>
            <div className={styles.field}>
              <label>Outline Color</label>
              <input
                type="color"
                value={selectedDialog.outlineColor || '#000000'}
                onChange={handleDialogOutlineColorChange}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label>Outline Width (px)</label>
            <input
              type="number"
              min={0}
              max={10}
              step={0.5}
              value={selectedDialog.outlineWidth ?? 2}
              onChange={handleDialogOutlineWidthChange}
            />
          </div>
        </div>
        </>
        )}

        {activeTab === 'transitions' && (
        <div className={styles.section}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>In</label>
              <select
                value={selectedDialog.inTransition || 'typing'}
                onChange={(e) => handleDialogInTransitionChange(e.target.value as DialogTransition)}
              >
                {DIALOG_TRANSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Out</label>
              <select
                value={selectedDialog.outTransition || 'fade'}
                onChange={(e) => handleDialogOutTransitionChange(e.target.value as DialogTransition)}
              >
                {DIALOG_TRANSITION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'properties' && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4>Lines</h4>
            <button className={styles.addBtn} onClick={handleAddLine}>
              + Add Line
            </button>
          </div>

          <div className={styles.linesList}>
            {selectedDialog.lines.map((line, index) => (
              <div key={line.id} className={styles.lineItem}>
                <span className={styles.lineIndex}>#{index + 1}</span>
                
                <div className={styles.lineContent}>
                  {editingLineId === line.id ? (
                    <div className={styles.lineEdit}>
                      <textarea
                        value={editingLineText}
                        onChange={(e) => setEditingLineText(e.target.value)}
                        placeholder="Enter dialog text..."
                        autoFocus
                      />
                      <div className={styles.editActions}>
                        <button onClick={handleSaveLineText}>Save</button>
                        <button onClick={() => setEditingLineId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={styles.lineText}
                      onClick={() => handleEditLineText(line.id, line.text)}
                    >
                      {line.text || <em>Click to add text...</em>}
                    </div>
                  )}

                  <div className={styles.lineAudio}>
                    {line.audioId ? (
                      <div className={styles.audioSet}>
                        <span>🔊 Audio #{line.audioId}</span>
                        <button onClick={() => handlePickAudio(line.id)}>Change</button>
                        <button onClick={() => handleRemoveAudio(line.id)}>×</button>
                        <VoiceLength
                          line={line}
                          inMs={lineInMs(selectedDialog, line)}
                          lengthMs={mediaLengths[line.audioId]}
                          known={!!mediaKnown[line.audioId]}
                          onFit={(ms) => fitLineToVoiceLength(selectedDialog.id, line.id, ms)}
                        />
                      </div>
                    ) : (
                      <button
                        className={styles.pickAudioBtn}
                        onClick={() => handlePickAudio(line.id)}
                      >
                        + Add Audio
                      </button>
                    )}
                  </div>

                  <div className={styles.lineDuration}>
                    <label>Start (s)</label>
                    <SecondsInput
                      valueMs={line.startTime}
                      onCommit={(ms) => handleLineStartTimeChange(line.id, ms)}
                      placeholder="auto"
                      title="When this line starts (from the dialog's content start); empty = right after the previous line"
                      testId="line-start"
                    />
                  </div>

                  <div className={styles.lineDuration}>
                    <label>Duration (s)</label>
                    <input
                      type="number"
                      min={0.1}
                      step={0.1}
                      value={line.duration ?? 3}
                      onChange={(e) => handleDurationChange(line.id, parseFloat(e.target.value))}
                      data-testid="line-duration"
                      title={line.audioId ? 'Seconds the line holds; Fit to voice sets it to the voice length plus a short pause' : 'Display duration in seconds'}
                    />
                  </div>
                </div>

                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDeleteLine(line.id)}
                  title="Delete line"
                >
                  ×
                </button>
              </div>
            ))}
            {selectedDialog.lines.length === 0 && (
              <div className={styles.emptyLines}>
                No lines yet. Click "Add Line" to create one.
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    );
  }

  // Sprite selected (existing code)
  if (!selectedSprite) {
    return null;
  }

  return (
    <SpritePropertiesSection
      sprite={selectedSprite}
      newStateName={newStateName}
      setNewStateName={(name) => { setNewStateName(name); setStateError(null); }}
      stateError={stateError}
      onNameChange={handleNameChange}
      onZIndexChange={handleZIndexChange}
      onAddState={handleAddState}
      onDeleteState={handleDeleteState}
      onSetDefaultState={handleSetDefaultState}
      onPickMedia={handlePickMedia}
      onImportSheet={handleImportSheet}
      onPickSheet={handlePickSheet}
      onEditSheet={handleEditSheet}
      updateSprite={updateSprite}
    />
  );
};

// Separate component for sprite properties to keep the main component cleaner
interface SpritePropertiesSectionProps {
  sprite: NonNullable<ReturnType<typeof useSceneStore.getState>['scene']>['sprites'][0];
  newStateName: string;
  setNewStateName: (name: string) => void;
  stateError: SpriteStateError | null;
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onZIndexChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddState: () => void;
  onDeleteState: (stateName: string) => void;
  onSetDefaultState: (stateName: string) => void;
  onPickMedia: (stateName: string) => void;
  /** Open the spritesheet import dialog for this sprite (spec 034), optionally aimed at one state (035). */
  onImportSheet: (targetState?: string) => void;
  /** Spec 035: pick a sheet from Media for a state. */
  onPickSheet: (stateName: string) => void;
  /** Spec 035: the sheet editor. */
  onEditSheet: (stateName: string, mediaId: number, assign: boolean) => void;
  updateSprite: ReturnType<typeof useSceneStore.getState>['updateSprite'];
}

const SpritePropertiesSection: React.FC<SpritePropertiesSectionProps> = ({
  sprite,
  newStateName,
  setNewStateName,
  stateError,
  onNameChange,
  onZIndexChange,
  onAddState,
  onDeleteState,
  onSetDefaultState,
  onPickMedia,
  onImportSheet,
  onPickSheet,
  onEditSheet,
  updateSprite,
}) => {
  const previewOrientation = useSceneStore((state) => state.previewOrientation) as Orientation;
  const renameSpriteState = useSceneStore((state) => state.renameSpriteState);

  // Get current position and transform for this orientation
  const currentPosition = getResponsivePosition(sprite.layout, previewOrientation);
  const currentTransform = getResponsiveTransform(sprite.layout, previewOrientation, sprite.transform);
  
  // Per-field override detection
  const isXOverride = isFieldOverride(sprite.layout, previewOrientation, 'x');
  const isYOverride = isFieldOverride(sprite.layout, previewOrientation, 'y');
  const isScaleOverride = isFieldOverride(sprite.layout, previewOrientation, 'scale');
  const isRotationOverride = isFieldOverride(sprite.layout, previewOrientation, 'rotation');
  
  // Base-layout writes go through the store (spec 005, via commitSpriteTransform): the basis orientation
  // updates the basis, any other orientation writes a field override; X and Y
  // clamp to −50…150 %.
  const setSpriteSize = useSceneStore((state) => state.setSpriteSize);
  const setSpriteHinge = useSceneStore((state) => state.setSpriteHinge);
  const apiClient = useApiClient();

  // Numeric fields commit like a canvas gesture (spec 019): an animated
  // property in Timeline mode is keyed at the playhead.
  const commitSpriteTransform = useSceneStore((state) => state.commitSpriteTransform);
  const handlePositionChange = useCallback((axis: 'x' | 'y', value: number) => {
    commitSpriteTransform(sprite.id, { position: { [axis]: value } });
  }, [sprite.id, commitSpriteTransform]);
  
  // Per-field override removal (↺): the field follows the basis again
  const handleRemoveOverride = useCallback((field: 'x' | 'y' | 'scale' | 'rotation') => {
    const newLayout = removeFieldOverride(sprite.layout, previewOrientation, field);
    updateSprite(sprite.id, { layout: newLayout });
  }, [sprite, previewOrientation, updateSprite]);
  
  const handleTransformChange = useCallback((property: 'scale' | 'rotation', value: number) => {
    commitSpriteTransform(sprite.id, { [property]: value });
  }, [sprite.id, commitSpriteTransform]);

  // Animated fields (Timeline mode, keyframes driving them here) show the
  // value at the playhead and a ◆ marker (spec 019).
  const playheadPosition = useSceneStore((state) => state.playheadPosition);
  useSceneStore((state) => state.keyframes);
  const { isPropertyAnimated, getKeyframeValueAt } = useSceneStore.getState();
  const animatedValue = (property: string, base: number): { value: number; animated: boolean } => {
    if (!isPropertyAnimated(sprite.id, property)) return { value: base, animated: false };
    const v = getKeyframeValueAt(sprite.id, property, Math.max(0, Math.round(playheadPosition)), previewOrientation);
    return { value: typeof v === 'number' ? v : base, animated: true };
  };
  const shownX = animatedValue('position.x', currentPosition.x);
  const shownY = animatedValue('position.y', currentPosition.y);
  const shownScale = animatedValue('transform.scale', currentTransform.scale);
  const shownRotation = animatedValue('transform.rotation', currentTransform.rotation || 0);
  // Spec 038: the flip playing at the playhead in this orientation (a stepped key, else the at-rest flip).
  const shownFlipX = animatedValue('transform.flipX', currentTransform.flipX ? 1 : 0);
  const shownFlipY = animatedValue('transform.flipY', currentTransform.flipY ? 1 : 0);
  const commitSpriteFlip = useSceneStore((state) => state.commitSpriteFlip);
  const animatedMarker = (label: string) => (
    <span className={styles.animatedMarker} title={`${label} is animated: edits add a keyframe at the playhead`} data-testid={`animated-${label.toLowerCase().replace(/\s+/g, '-')}`}>◆</span>
  );

  // Size (global, canvas px) with an aspect lock, on by default
  const size = resolveSpriteSize(sprite);
  const [aspectLocked, setAspectLocked] = useState(true);
  const [matchError, setMatchError] = useState<string | null>(null);
  const handleSizeChange = useCallback((axis: 'width' | 'height', raw: number) => {
    if (!(raw > 0)) return;
    const value = Math.round(raw);
    if (!aspectLocked) {
      setSpriteSize(sprite.id, { ...size, [axis]: value });
      return;
    }
    const ratio = size.width / size.height;
    setSpriteSize(sprite.id, axis === 'width'
      ? { width: value, height: Math.max(1, Math.round(value / ratio)) }
      : { width: Math.max(1, Math.round(value * ratio)), height: value });
  }, [aspectLocked, size, sprite.id, setSpriteSize]);

  // "Match image": re-derive the size from the default state's image
  const defaultStateMediaId = sprite.states.find((st) => st.name === sprite.defaultState)?.mediaId ?? 0;
  const handleMatchImage = useCallback(async () => {
    if (!(defaultStateMediaId > 0)) return;
    setMatchError(null);
    try {
      const [natural] = await Promise.all([
        loadImageNaturalSize(await apiClient.getMediaFileBlob(defaultStateMediaId)),
        useMediaSheetStore.getState().loadSheets([defaultStateMediaId]),
      ]);
      // A spritesheet matches its frame, never the whole sheet (spec 033).
      setSpriteSize(sprite.id, sizeFromImage(pictureSizeOf(defaultStateMediaId, natural)));
    } catch {
      setMatchError('The image could not be read.');
    }
  }, [defaultStateMediaId, apiClient, sprite.id, setSpriteSize]);

  // Hinge (global). Shows the interpolated hinge when keyframes exist.
  const getInterpolatedValue = useSceneStore((state) => state.getInterpolatedValue);
  const interpHingeX = getInterpolatedValue(sprite.id, 'hinge.x', playheadPosition, previewOrientation) as number | undefined;
  const interpHingeY = getInterpolatedValue(sprite.id, 'hinge.y', playheadPosition, previewOrientation) as number | undefined;
  const baseHinge = sprite.hinge || { x: 0.5, y: 0.5 };
  const currentHinge = {
    x: interpHingeX !== undefined ? interpHingeX / 100 : baseHinge.x,
    y: interpHingeY !== undefined ? interpHingeY / 100 : baseHinge.y,
  };
  const isHingeCustom = currentHinge.x !== 0.5 || currentHinge.y !== 0.5;

  // Changing the hinge keeps the sprite where it is (compensateHinge in the store)
  const handleHingeChange = useCallback((axis: 'x' | 'y', value: number) => {
    setSpriteHinge(sprite.id, { ...baseHinge, [axis]: Math.max(0, Math.min(1, value)) });
  }, [sprite.id, baseHinge, setSpriteHinge]);
  
  const handleHingePreset = useCallback((x: number, y: number) => {
    setSpriteHinge(sprite.id, { x, y });
  }, [sprite.id, setSpriteHinge]);
  
  const handleResetHinge = useCallback(() => {
    setSpriteHinge(sprite.id, undefined);
  }, [sprite.id, setSpriteHinge]);

  return (
    <div className={styles.panel}>
      <>
      <div className={styles.section}>
        
        <div className={styles.field}>
          <label>Name</label>
          <input
            type="text"
            value={sprite.name}
            onChange={onNameChange}
          />
        </div>

        <div className={styles.field}>
          <label>Z-Index</label>
          <input
            type="number"
            value={sprite.zIndex}
            onChange={onZIndexChange}
            min={0}
            step={1}
          />
        </div>
      </div>

      {/* Size Section - global canvas px (spec 005) */}
      <div className={styles.section}>
        <div className={styles.fieldGroupHeader}>
          <h4>Size (px)</h4>
          <button
            className={styles.resetButton}
            onClick={handleMatchImage}
            disabled={!(defaultStateMediaId > 0)}
            title="Set the size from the default state's image (longer side 160 px)"
          >
            Match image
          </button>
        </div>
        <div className={styles.inputRow}>
          <div className={styles.inputWithLabel}>
            <label htmlFor={`sprite-w-${sprite.id}`}>W</label>
            <input
              id={`sprite-w-${sprite.id}`}
              aria-label="Width"
              type="number"
              value={size.width}
              min={1}
              onChange={(e) => handleSizeChange('width', parseFloat(e.target.value))}
            />
          </div>
          <button
            className={`${styles.aspectLock} ${aspectLocked ? styles.active : ''}`}
            onClick={() => setAspectLocked((v) => !v)}
            aria-pressed={aspectLocked}
            aria-label="Lock aspect ratio"
            title={aspectLocked ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
          >
            {aspectLocked ? '🔒' : '🔓'}
          </button>
          <div className={styles.inputWithLabel}>
            <label htmlFor={`sprite-h-${sprite.id}`}>H</label>
            <input
              id={`sprite-h-${sprite.id}`}
              aria-label="Height"
              type="number"
              value={size.height}
              min={1}
              onChange={(e) => handleSizeChange('height', parseFloat(e.target.value))}
            />
          </div>
        </div>
        {matchError && <span className={styles.fieldHint}>{matchError}</span>}
      </div>

      {/* Hinge Section - global (not per-orientation) */}
      <div className={styles.section}>
        <div className={styles.fieldGroupHeader}>
          <h4>Hinge Point (%)</h4>
          {isHingeCustom && (
            <button
              className={styles.resetButton}
              onClick={handleResetHinge}
              title="Reset to center"
            >
              ↺ Reset
            </button>
          )}
        </div>
        
        <div className={styles.hingePresets}>
          {/* 3x3 grid of preset buttons */}
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0 && currentHinge.y === 0 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0, 0)}
            title="Top-Left"
          >◤</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0.5 && currentHinge.y === 0 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0.5, 0)}
            title="Top-Center"
          >▲</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 1 && currentHinge.y === 0 ? styles.active : ''}`}
            onClick={() => handleHingePreset(1, 0)}
            title="Top-Right"
          >◥</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0 && currentHinge.y === 0.5 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0, 0.5)}
            title="Center-Left"
          >◀</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0.5 && currentHinge.y === 0.5 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0.5, 0.5)}
            title="Center"
          >●</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 1 && currentHinge.y === 0.5 ? styles.active : ''}`}
            onClick={() => handleHingePreset(1, 0.5)}
            title="Center-Right"
          >▶</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0 && currentHinge.y === 1 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0, 1)}
            title="Bottom-Left"
          >◣</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 0.5 && currentHinge.y === 1 ? styles.active : ''}`}
            onClick={() => handleHingePreset(0.5, 1)}
            title="Bottom-Center"
          >▼</button>
          <button 
            className={`${styles.hingePreset} ${currentHinge.x === 1 && currentHinge.y === 1 ? styles.active : ''}`}
            onClick={() => handleHingePreset(1, 1)}
            title="Bottom-Right"
          >◢</button>
        </div>
        
        <div className={styles.inputRow}>
          <div className={styles.inputWithLabel}>
            <label>X</label>
            <input
              type="number"
              value={Math.round(currentHinge.x * 100)}
              onChange={(e) => handleHingeChange('x', (parseFloat(e.target.value) || 0) / 100)}
              step={1}
              min={0}
              max={100}
            />
          </div>
          <div className={styles.inputWithLabel}>
            <label>Y</label>
            <input
              type="number"
              value={Math.round(currentHinge.y * 100)}
              onChange={(e) => handleHingeChange('y', (parseFloat(e.target.value) || 0) / 100)}
              step={1}
              min={0}
              max={100}
            />
          </div>
        </div>
      </div>

      {/* Position & Transform Section - orientation aware */}
      <div className={styles.section}>
        <h4>Position & Transform ({previewOrientation})</h4>
        
        {/* Position fields — per-field override badges */}
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <label>Position (%)</label>
          </div>
          <div className={styles.inputRow}>
            <div className={styles.inputWithLabel}>
              <label>X</label>
              {shownX.animated && animatedMarker('X')}
              {isXOverride && (
                <button
                  className={styles.overrideIndicator}
                  onClick={() => handleRemoveOverride('x')}
                  title={`Override for ${previewOrientation}. Click to follow the basis again.`}
                  aria-label="Reset X override"
                >
                  ↺
                </button>
              )}
              <input
                type="number"
                aria-label="Position X"
                value={Math.round(shownX.value * 10) / 10}
                onChange={(e) => handlePositionChange('x', parseFloat(e.target.value) || 0)}
                step={0.5}
                min={SPRITE_POSITION_RANGE[0]}
                max={SPRITE_POSITION_RANGE[1]}
              />
            </div>
            <div className={styles.inputWithLabel}>
              <label>Y</label>
              {shownY.animated && animatedMarker('Y')}
              {isYOverride && (
                <button
                  className={styles.overrideIndicator}
                  onClick={() => handleRemoveOverride('y')}
                  title={`Override for ${previewOrientation}. Click to follow the basis again.`}
                  aria-label="Reset Y override"
                >
                  ↺
                </button>
              )}
              <input
                type="number"
                aria-label="Position Y"
                value={Math.round(shownY.value * 10) / 10}
                onChange={(e) => handlePositionChange('y', parseFloat(e.target.value) || 0)}
                step={0.5}
                min={SPRITE_POSITION_RANGE[0]}
                max={SPRITE_POSITION_RANGE[1]}
              />
            </div>
          </div>
        </div>

        {/* Scale field */}
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <label>Scale</label>
            {shownScale.animated && animatedMarker('Scale')}
            {isScaleOverride && (
              <button
                className={styles.overrideIndicator}
                onClick={() => handleRemoveOverride('scale')}
                title={`Override for ${previewOrientation}. Click to follow the basis again.`}
                aria-label="Reset Scale override"
              >
                ↺ Override
              </button>
            )}
          </div>
          <div className={styles.inputRow}>
            <input
              type="number"
              aria-label="Scale"
              value={Math.round(shownScale.value * 100) / 100}
              onChange={(e) => handleTransformChange('scale', parseFloat(e.target.value) || 1)}
              step={0.1}
              min={0.1}
              max={10}
            />
          </div>
        </div>

        {/* Rotation field */}
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <label>Rotation (°)</label>
            {shownRotation.animated && animatedMarker('Rotation')}
            {isRotationOverride && (
              <button
                className={styles.overrideIndicator}
                onClick={() => handleRemoveOverride('rotation')}
                title={`Override for ${previewOrientation}. Click to follow the basis again.`}
                aria-label="Reset Rotation override"
              >
                ↺ Override
              </button>
            )}
          </div>
          <div className={styles.inputRow}>
            <input
              type="number"
              aria-label="Rotation"
              value={Math.round(shownRotation.value * 10) / 10}
              onChange={(e) => handleTransformChange('rotation', parseFloat(e.target.value) || 0)}
              step={1}
            />
          </div>
        </div>

        {/* Flips (spec 038): the picture mirrored inside its box */}
        <div className={styles.fieldGroup}>
          <div className={styles.fieldGroupHeader}>
            <label>Flip</label>
          </div>
          <div className={styles.inputRow}>
            {([['x', 'Flip X', shownFlipX], ['y', 'Flip Y', shownFlipY]] as const).map(([axis, label, shown]) => {
              const on = shown.value !== 0;
              return (
                <button
                  key={axis}
                  type="button"
                  className={`${styles.flipToggle} ${on ? styles.active : ''}`}
                  data-testid={`flip-${axis}`}
                  aria-pressed={on}
                  title={`${label}: mirror the picture ${axis === 'x' ? 'left to right' : 'top to bottom'}`}
                  onClick={() => commitSpriteFlip(sprite.id, axis, !on)}
                >
                  {label}
                  {shown.animated && animatedMarker(label)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      </>

      <MotionSection sprite={sprite} />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4>States</h4>
        </div>

        <StatesSection
          states={sprite.states}
          defaultState={sprite.defaultState}
          onSetDefault={onSetDefaultState}
          onDelete={onDeleteState}
          onPickMedia={onPickMedia}
          onRename={(from, to) => stateErrorText(renameSpriteState(sprite.id, from, to), 'sprite')}
          sheets={{ spriteId: sprite.id, onImportSheet, onPickSheet, onEditSheet }}
        />

        <div className={styles.addState}>
          <input
            type="text"
            value={newStateName}
            onChange={(e) => setNewStateName(e.target.value)}
            placeholder="New state name..."
            onKeyDown={(e) => e.key === 'Enter' && onAddState()}
          />
          <button onClick={onAddState} disabled={!newStateName.trim()}>
            Add
          </button>
          <button onClick={() => onImportSheet()} data-testid="import-spritesheet">
            Import spritesheet…
          </button>
        </div>
        {stateError && (
          <span className={styles.fieldHint} role="alert">{stateErrorText(stateError, 'sprite')}</span>
        )}
      </div>
    </div>
  );
};

export default PropertiesPanel;
